import React, { useContext } from "react";
import { I18nReactContext } from "../../../../contexts/I18nContext";
import { Button } from "../../../Form/Button/Button";
import { Modal } from "../../../Smart/Modal/Modal";
import { buttonColors } from "../../../Pickers/InputTheme";
import instructionHive from "../../../../assets/images/session/instruction-hive.png";
import beeInstruction from "../../../../assets/images/session/bee-instruction.png";
import './InstructionModal.scss';

/**
 * InstructionModal Component
 * Shared component for displaying instructions before starting a survey or questionnaire.
 * 
 * @param {object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback when closing the modal
 * @param {string} props.title - Title of the modal
 * @param {React.ReactNode} props.children - Content of the modal (instructions, lists, etc.)
 * @param {string} props.footerText - Text to display at the bottom
 * @param {string} props.buttonText - Text for the start button
 * @param {Function} props.onButtonClick - Callback when clicking the start button
 * @param {boolean} props.buttonDisabled - Whether the start button is disabled
 * @param {boolean} props.loading - Whether to show a loading message
 * @param {string} [props.backgroundImage] - Optional background image URL
 * @param {string} [props.illustration] - Optional illustration image URL
 * @returns {JSX.Element} The InstructionModal component
 */
export const InstructionModal = ({ 
    open, 
    onClose = () => {}, 
    title, 
    children, 
    footerText,
    buttonText,
    onButtonClick,
    buttonDisabled = false,
    loading = false,
    backgroundImage = instructionHive,
    illustration = beeInstruction
}) => {
    const { i18n } = useContext(I18nReactContext);

    return (
        <Modal open={open} onClose={onClose} hideCloseButton={true}>
            <div className="instructionModal">
                <div
                    className={`modalBackground`}
                    style={{backgroundImage: `url(${backgroundImage})`}}
                >
                    <div className="contentWrapper">
                        <div className="instructionTextContainer">
                            {title && <h2 className="instructionTitle">{title}</h2>}
                            
                            {children}
                            
                            {footerText && <p className="instructionFooter">{footerText}</p>}
                        </div>
                        <div className="instructionImageContainer">
                            <img src={illustration} alt="Bee" className="beeImage" />
                        </div>
                    </div>
                    <div className="instructionButtonContainer">
                        <Button
                            color={buttonColors.GOLD}
                            onClick={onButtonClick}
                            text={buttonText}
                            disabled={buttonDisabled}
                        />
                        {loading && <div className="loading-message">{i18n.translate('learningCenter.loading')}</div>}
                    </div>
                </div>
            </div>
        </Modal>
    );
};
